import imageFiles from '../components/certImages.json'
import { getCookie } from '../components/common/cookie'
import {
  isAUAUDCert,
  isAUAUDCred,
  isAUAUDElfCert,
  isAUAUDElfCred,
  isCred,
  isMermaid,
  isMermaidPerformancer,
  isMermaidPerformancerInstructor,
  isMidwayDiver,
} from '../utils/cert'

const defaultImage = '-1'

const getImageFileById = (id) => {
  if ('rd' === id) return id
  const match = imageFiles.filter((i) => i.id === '' + id)
  return match.length > 0 ? match[0].file || defaultImage : defaultImage
}

/**
 * 把资源基名拼成本地化文件名。
 *
 * CHNA-2776：卡面多语言变体只有 `-sc`/`-tc` 两种（11 个生肖基名 × 2，现全部托管在
 * `ECARD_IMG_CDN_BASE_URL`）。旧实现只把 `en` 映射成 `sc`，其余语言原样拼进去 ⇒ cookie 还没写
 * （首帧）或值异常时会拼出 `year2026-undefined.jpg`（当时是随包资源，会抛 `Cannot find module`）。
 * 现在只有 `tc` 取 `-tc`，其余（`sc`/`en`/缺失/未知）一律回落 `-sc`。
 */
export const localizeResource = (path, language) => {
  if (!path) return path
  const [tail, ...rest] = path.split('.').reverse()
  // 已带语言后缀的值先去后缀再重拼：否则 `-sc.jpg` 会变成 `-sc-sc.jpg`
  // （库里两种形态混用、或有人手工改过数据时的幂等保护）
  const base = rest
    .reverse()
    .join('.')
    .replace(/-(sc|tc)$/i, '')
  return `${base}-${language === 'tc' ? 'tc' : 'sc'}.${tail}`
}

export const getImageIdFromCardStockId = (ecard) => {
  let id
  // if(isJuniorMermaid(ecard)) return 1;
  if (isMermaid(ecard)) {
    id = isMermaidPerformancer(ecard) || isMermaidPerformancerInstructor(ecard) ? 5245 : ecard.typeNumber
  } else if (isMidwayDiver(ecard)) {
    id = ecard.typeNumber
  } else if (isAUAUDElfCert(ecard) || isAUAUDElfCred(ecard)) {
    id = 'cert_AUA_UD_Elf'
  } else if (isAUAUDCert(ecard)) {
    id = 'cert_AUA_UD'
  } else if (isAUAUDCred(ecard)) {
    id = 'cert_AUA_UD_I'
  } else if ('Resort Diver' === ecard.ecardName) {
    id = 'rd'
  } else {
    id = ecard.printCardTypeId || -1
    if (ecard.issueDate && ecard.issueDate.indexOf('2026') >= 0) {
      if (isCred(ecard)) {
        if (id === 1) {
          id = 162
        }
      } else {
        if (id === -2) {
          id = 163
        } else if (id === -1 || id === 1) {
          id = 161
        }
      }
    } else {
      if (isCred(ecard) && id === -1) id = 1
    }
  }
  return id
}

export const getPlasticCardImagePath = (ecard) => {
  const id = getImageIdFromCardStockId(ecard)
  return `certs/cert_${getImageFileById(id)}.jpg`
}

export const getEcardImagePathArr = (ecard) => {
  const customizedImages = ecard.customizedImages || []
  const ecardImages = ecard.ecardImages || []
  return customizedImages.map((c) => c.imgUrl).concat(ecardImages.map(getEcardImagePath))
}

/**
 * 卡面多语言变体（`<逻辑键>-sc.jpg` / `-tc.jpg`）的 CDN 基址。
 *
 * CHNA-2776：这 22 张图（11 个生肖基名 × 2 个语言）已全量托管到该 CDN（`china-club-img` 桶，
 * 与后端 `EcardImageUrlResolver.VARIANT_BASE_URL`、club-web 小程序取图同一个键），
 * **仓内不再保留副本**（原 `src/assets/images/ecard_img/` 已删除）⇒ 这里直接给绝对 URL，
 * 不再走 `require` 的随包资源（顺带消掉 webpack 动态 require 在文件缺失时抛
 * `Cannot find module` 的风险）。
 */
export const ECARD_IMG_CDN_BASE_URL = 'https://club-img.padi.com.cn/static/ecards/ecard_img'

/**
 * 是否绝对 URL——与后端 `EcardImageUrlResolver#isAbsoluteUrl` **同判据**：`http://`/`https://` 开头，
 * **忽略大小写与首尾空白**。
 *
 * 为什么必须与后端一致（PR #10 review）：后端按该判据决定「不生成 `imageUrls`、把 `imgFile` 原样透出」，
 * 取图侧若只认小写 `http` 前缀，`HTTPS://…` 或带空白的值就会被当成相对逻辑名、拼成无效 CDN 路径。
 */
export const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value ?? '').trim())

const getEcardImagePath = ({ imgFile }) => {
  if (isAbsoluteUrl(imgFile)) {
    return imgFile
  }
  return `${ECARD_IMG_CDN_BASE_URL}/${localizeResource(imgFile, getCookie('language'))}`
}

export const getCoverImagePath = (ecard) => {
  // 自定义卡面
  const customizedImg = ecard?.customizedImages?.find((c) => c.active === true)
  if (customizedImg) {
    return customizedImg.imgUrl
  }
  // 电子卡面
  const ecardImg = ecard?.ecardImages?.find((ecardImg) => ecardImg.active === true)
  if (ecardImg) {
    return getEcardImagePath(ecardImg)
  }
  // 其他卡面
  return getPlasticCardImagePath(ecard)
}

export const getCertsImagePath = ({ imgFile, imgUrl, desc }, ecard) => {
  if (isAbsoluteUrl(imgFile) || isAbsoluteUrl(imgUrl)) {
    return imgFile || imgUrl
  }
  if (desc === '普通版') {
    return getPlasticCardImagePath(ecard)
  }
  return getEcardImagePath({ imgFile: imgFile || imgUrl })
}

export const finalImagePath = (cert, path) => {
  const { certificationNumber, credentialNumber, storeNumber } = cert
  if (isAbsoluteUrl(path)) {
    return path
  } else if (certificationNumber && certificationNumber === 58 && storeNumber && storeNumber === 23724) {
    return require(`../assets/images/certs/cert_quwan.jpg`)
  } else if ((isAUAUDElfCert(cert) || isAUAUDElfCred(cert)) && path.indexOf('certs/') === 0) {
    return require(`../assets/images/certs/cert_AUA_UD_Elf.jpg`)
  } else if (certificationNumber && isAUAUDCert(cert) && path.indexOf('certs/') === 0) {
    return require(`../assets/images/certs/cert_AUA_UD.jpg`)
  } else if (credentialNumber && isAUAUDCred(cert) && path.indexOf('certs/') === 0) {
    return require(`../assets/images/certs/cert_AUA_UD_I.jpg`)
  } else {
    if (certificationNumber && path === 'certs/cert_1.jpg') return require(`../assets/images/certs/cert_-1.jpg`)
    return require(`../assets/images/${path}`)
  }
}

/**
 * 卡面图取图的**统一入口**（CHNA-2776）：接受任意形态的 `img_file`，返回可直接渲染的地址。
 *
 * - 绝对 URL → 原样；
 * - `普通版` / `cert_*`（cert 资源，不是卡面逻辑名）→ `certs/<名>`（随包资源）；
 * - 相对逻辑名（生肖年卡面，如 `year2026.jpg`）→ CDN 上的 `<键>-<lang>.jpg`（`ECARD_IMG_CDN_BASE_URL`）。
 *
 * 为什么要有它：`img_file` 有「绝对 URL / 相对逻辑名」两种形态（口径见后端
 * `shared/self/api-contract.md`），而若干页面此前把 `imgFile` 直接当 `<img src>`、
 * 或直接 `require('.../ecard_img/' + imgFile)` 拼裸名——前者把相对名拼到当前路由下 ⇒ 404；
 * 后者仓内只有 `-sc`/`-tc` 变体 ⇒ 直接抛 `Cannot find module`。
 * 所有「不走证书详情链路」的取图点都必须经这里（有源码级守卫钉住，见
 * `cardFaceImageUrlWiring.test.js`）。
 */
export const getCardFaceImageUrl = (imgFile, desc) => {
  if (!imgFile) return ''
  if (isAbsoluteUrl(imgFile)) return imgFile
  const isCertFile = desc === '普通版' || imgFile.indexOf('cert_') === 0
  return isCertFile ? finalImagePath({}, `certs/${imgFile}`) : getEcardImagePath({ imgFile })
}

export const getPlasticActiveStatus = ({ customizedImages, ecardImages }) => {
  let newCustomizedImages = customizedImages || []
  let newEcardImages = ecardImages || []
  let allCards = newCustomizedImages.concat(newEcardImages)
  return !allCards.find((card) => card.active)
}

const certStockIdMappings = {
  CERTIFICATION: [
    { id: 4160, cardStockTypeId: 77 },
    { id: 4161, cardStockTypeId: 77 },
    { id: 4162, cardStockTypeId: 77 },
    { id: 4163, cardStockTypeId: 77 },
    { id: 17, cardStockTypeId: 8 },
    { id: 29, cardStockTypeId: 11 },
    { id: 38, cardStockTypeId: 8 },
    { id: 51, cardStockTypeId: 8 },
    { id: 62, cardStockTypeId: 8 },
    { id: 74, cardStockTypeId: 8 },
    { id: 3147, cardStockTypeId: 8 },
    { id: 3148, cardStockTypeId: 8 },
    { id: 3149, cardStockTypeId: 8 },
    { id: 3150, cardStockTypeId: 8 },
    { id: 4, cardStockTypeId: 9 },
    { id: 85, cardStockTypeId: 10 },
    { id: 3151, cardStockTypeId: 83 },
  ],
  CREDENTIAL: [
    { id: 9430, cardStockTypeId: 83 },
    { id: 9403, cardStockTypeId: 83 },
    { id: 11362, cardStockTypeId: 77 },
    { id: 11363, cardStockTypeId: 77 },
    { id: 11364, cardStockTypeId: 77 },
    { id: 11365, cardStockTypeId: 77 },
  ],
}

export const getStockTypeIdByCertId = (type, certId) => {
  return certStockIdMappings[type].filter((m) => m.id === certId).map((m) => m.cardStockTypeId)[0]
}
